import { Skull, Flame } from "lucide-react";
import "@/gm/gm-animations.css";

/**
 * Dedicated dark-fantasy World Boss illustration. Compared to the small
 * procedural `EnemySprite` used for random NPC combat, this one is bigger,
 * more theatrical, and hue-shifted per boss name so the three weekly
 * bosses each feel visually distinct.
 *
 * The whole composition is CSS/SVG so no image assets are required:
 *   - Immense hooded silhouette with layered cape + horns
 *   - Chains dangling from the torso
 *   - Molten cracks glowing from underneath
 *   - Rune circle at the feet
 *   - Two burning eye pits
 *
 * Props:
 *   name  — boss name, used to hash a hue
 *   pct   — 0-100, drives the "wounded" filter (dims + reddens as HP falls)
 *   size  — outer container size in px (default 220)
 */
export default function BossSprite({ name = "boss", pct = 100, size = 220, image = null, animations = [] }) {
  const nameHash = name.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 5381);
  const hue = nameHash % 360;
  const tint = `hsl(${hue}, 55%, 30%)`;
  const tintBright = `hsl(${hue}, 75%, 55%)`;
  const wounded = pct < 100 ? Math.max(0.3, pct / 100) : 1;
  const shakeClass = pct > 0 && pct < 25 ? "boss-anguish" : "";
  const w = size;
  const h = Math.round(size * 1.15);
  const gmList = Array.isArray(animations) ? (animations.length ? animations : ["idle-float"]) : null;
  const gmAnims = gmList ? gmList.map(a => `gm-anim-${a}`).join(" ") : "";
  const gmKeyframes = {
    "idle-float": ["gm-float", "3.4s", "ease-in-out"],
    "idle-pulse": ["gm-pulse", "2.2s", "ease-in-out"],
    "idle-breathe": ["gm-breathe", "3.8s", "ease-in-out"],
    "idle-wobble": ["gm-wobble", "4.5s", "ease-in-out"],
    "hover-stretch": ["gm-auto-stretch", "2.8s", "ease-in-out"],
    "hover-compress": ["gm-auto-compress", "2.8s", "ease-in-out"],
    "hover-rotate": ["gm-auto-rotate", "3s", "ease-in-out"],
    "hover-glow": ["gm-auto-glow", "2.4s", "ease-in-out"],
    "hover-shake": ["gm-auto-shake", "3.5s", "ease-in-out"],
  };
  const gmAnimation = gmList
    ? gmList.map(a => { const [k,d,e] = gmKeyframes[a] || gmKeyframes["idle-float"]; return `${k} ${d} ${e} infinite`; }).join(", ")
    : null;

  // GM artwork is authoritative when configured. The procedural boss art
  // below remains the fallback for legacy/unconfigured bosses.
  if (image) {
    return (
      <div
        className={`relative select-none pointer-events-none ${shakeClass} ${gmAnims}`}
        style={{ width: w, height: h, filter: `saturate(${wounded}) brightness(${0.6 + wounded * 0.4})`, animation: gmAnimation || undefined }}
        data-testid="boss-gm-image"
      >
        <img
          src={image}
          alt={name}
          draggable={false}
          className="w-full h-full object-contain select-none"
          style={{ filter: "drop-shadow(0 10px 14px rgba(0,0,0,.85))" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
    );
  }

  // Chains
  const chain = (dx) => (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `calc(50% + ${dx}px)`,
        top: "38%",
        width: 4,
        height: "48%",
        background: `repeating-linear-gradient(180deg, #2a2a2a 0, #2a2a2a 6px, #050505 6px, #050505 10px)`,
        transform: "translateX(-50%)",
        boxShadow: "0 0 4px rgba(0,0,0,0.9)",
      }}
    />
  );

  return (
    <div
      className={`relative select-none pointer-events-none ${shakeClass} ${gmAnims}`}
      style={{ width: w, height: h, filter: `saturate(${wounded}) brightness(${0.6 + wounded * 0.4})`, animation: gmAnimation || undefined }}
      data-testid="boss-sprite"
    >
      {/* Molten rune circle beneath */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", bottom: "-4%",
          transform: "translateX(-50%)",
          width: w * 0.9, height: h * 0.14,
          borderRadius: "50%",
          background: `radial-gradient(50% 100% at 50% 50%, ${tintBright}88, ${tint}55 40%, transparent 70%)`,
          animation: "boss-rune 4s ease-in-out infinite",
        }}
      />
      {/* Rune ring outline */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", bottom: 0,
          transform: "translateX(-50%)",
          width: w * 0.75, height: h * 0.10,
          borderRadius: "50%",
          border: `1px solid ${tintBright}77`,
          boxShadow: `0 0 12px ${tintBright}55, inset 0 0 8px ${tintBright}33`,
        }}
      />

      {/* Big cape (fanned out) */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", top: "22%",
          transform: "translateX(-50%)",
          width: w * 0.98, height: h * 0.72,
          background: `linear-gradient(180deg, #050505 0%, ${tint} 55%, #050505 100%)`,
          clipPath: "polygon(50% 0, 92% 20%, 100% 100%, 65% 92%, 50% 100%, 35% 92%, 0 100%, 8% 20%)",
          boxShadow: `0 0 30px ${tint}, inset 0 0 24px rgba(0,0,0,0.8)`,
          animation: "boss-breathe 4s ease-in-out infinite",
        }}
      />

      {/* Torso silhouette */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", top: "32%",
          transform: "translateX(-50%)",
          width: w * 0.55, height: h * 0.44,
          background: `linear-gradient(180deg, #050505, #0a0a0a)`,
          clipPath: "polygon(30% 0, 70% 0, 82% 40%, 88% 100%, 12% 100%, 18% 40%)",
          boxShadow: `inset 0 0 24px rgba(0,0,0,0.9), 0 0 12px ${tint}88`,
        }}
      />

      {/* Molten cracks on torso */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", top: "42%",
          transform: "translateX(-50%)",
          width: w * 0.35, height: 2,
          background: tintBright,
          boxShadow: `0 0 6px ${tintBright}, 0 0 12px ${tintBright}88`,
          animation: "boss-flicker 2.5s ease-in-out infinite",
        }}
      />
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "40%", top: "56%",
          width: w * 0.20, height: 2,
          background: tintBright,
          boxShadow: `0 0 6px ${tintBright}, 0 0 12px ${tintBright}88`,
          animation: "boss-flicker 3.1s ease-in-out infinite",
        }}
      />

      {/* Chains */}
      {chain(-Math.round(w * 0.18))}
      {chain(Math.round(w * 0.18))}

      {/* Hood */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: "50%", top: "10%",
          transform: "translateX(-50%)",
          width: w * 0.42, height: h * 0.30,
          background: `linear-gradient(180deg, #0a0a0a, #050505)`,
          clipPath: "polygon(20% 100%, 30% 20%, 50% 0, 70% 20%, 80% 100%)",
          boxShadow: `0 0 18px ${tint}66`,
        }}
      />

      {/* Horns */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: `calc(50% - ${w * 0.16}px)`,
          top: "8%",
          width: w * 0.10, height: h * 0.14,
          background: `linear-gradient(180deg, ${tint}, #0a0a0a)`,
          clipPath: "polygon(20% 100%, 45% 30%, 80% 0, 60% 40%, 60% 100%)",
          transform: "rotate(-15deg)",
          filter: `drop-shadow(0 0 4px ${tint})`,
        }}
      />
      <div
        aria-hidden
        className="absolute"
        style={{
          right: `calc(50% - ${w * 0.16}px)`,
          top: "8%",
          width: w * 0.10, height: h * 0.14,
          background: `linear-gradient(180deg, ${tint}, #0a0a0a)`,
          clipPath: "polygon(80% 100%, 55% 30%, 20% 0, 40% 40%, 40% 100%)",
          transform: "rotate(15deg)",
          filter: `drop-shadow(0 0 4px ${tint})`,
        }}
      />

      {/* Skull face inside the hood */}
      <Skull
        className="absolute"
        style={{
          left: "50%", top: "18%",
          transform: "translateX(-50%)",
          width: w * 0.28, height: w * 0.28,
          color: "#050505",
          filter: `drop-shadow(0 0 6px ${tintBright}) drop-shadow(0 0 2px ${tintBright})`,
        }}
        strokeWidth={1.1}
      />

      {/* Burning eyes */}
      <div
        className="absolute"
        style={{
          left: `calc(50% - ${w * 0.05}px)`,
          top: "24%",
          width: 8, height: 8,
          borderRadius: "50%",
          background: tintBright,
          boxShadow: `0 0 8px ${tintBright}, 0 0 16px ${tintBright}88`,
          animation: "boss-flicker 1.6s ease-in-out infinite",
        }}
      />
      <div
        className="absolute"
        style={{
          left: `calc(50% + ${w * 0.02}px)`,
          top: "24%",
          width: 8, height: 8,
          borderRadius: "50%",
          background: tintBright,
          boxShadow: `0 0 8px ${tintBright}, 0 0 16px ${tintBright}88`,
          animation: "boss-flicker 1.9s ease-in-out infinite",
        }}
      />

      {/* Floating embers */}
      {[0, 1, 2, 3, 4].map(i => (
        <span
          key={i}
          aria-hidden
          className="absolute"
          style={{
            left: `${20 + (nameHash + i * 47) % 60}%`,
            top: `${60 + (i * 8) % 30}%`,
            width: 3, height: 3,
            background: tintBright,
            borderRadius: "50%",
            boxShadow: `0 0 4px ${tintBright}`,
            animation: `boss-ember ${3 + (i % 3)}s ease-in-out ${(i * 0.4).toFixed(1)}s infinite`,
            opacity: 0.7,
          }}
        />
      ))}

      {/* Fire flag at the base */}
      <Flame
        className="absolute"
        style={{
          left: "50%", bottom: "6%",
          transform: "translateX(-50%)",
          width: 24, height: 24,
          color: tintBright,
          filter: `drop-shadow(0 0 6px ${tintBright})`,
          opacity: 0.8,
        }}
        strokeWidth={1.2}
      />

      <style>{`
        @keyframes boss-breathe {
          0%,100% { transform: translateX(-50%) scale(1); }
          50%     { transform: translateX(-50%) scale(1.02); }
        }
        @keyframes boss-flicker {
          0%,100% { opacity: 1; filter: brightness(1); }
          40%     { opacity: 0.6; }
          60%     { opacity: 1; filter: brightness(1.4); }
        }
        @keyframes boss-rune {
          0%,100% { opacity: 0.85; transform: translateX(-50%) scale(1); }
          50%     { opacity: 1; transform: translateX(-50%) scale(1.06); }
        }
        @keyframes boss-ember {
          0%   { transform: translateY(0); opacity: 0.85; }
          70%  { opacity: 0.4; }
          100% { transform: translateY(-40px); opacity: 0; }
        }
        @keyframes boss-anguish {
          0%,100% { transform: translateX(0); }
          25%     { transform: translateX(-3px); }
          75%     { transform: translateX(3px); }
        }
        .boss-anguish { animation: boss-anguish 1.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
